// src/pages/dashboard/talent/MediaGallery.js
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Spinner, Alert, Button, Modal } from 'react-bootstrap';
import { Image, Film, Trash2, Camera, ShieldAlert, Play, Eye, X } from 'lucide-react';
import Swal from 'sweetalert2';
import api from '../../../services/api';
import './MediaGallery.css';

const MediaGallery = () => {
    const [mediaItems, setMediaItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // 🟢 Modals State for Photo Preview & Video Playback
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [activePhotoUrl, setActivePhotoUrl] = useState('');
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [activeVideoUrl, setActiveVideoUrl] = useState('');

    const fetchMedia = async () => {
        setLoading(true);
        try {
            const res = await api.get('/talent-dashboard/media-gallery');
            setMediaItems(res.data.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMedia();
    }, []);

    // Filter photos and videos
    const photos = mediaItems.filter(item => item.media_type === 'image');
    const videos = mediaItems.filter(item => item.media_type === 'video');

    // Condition: Check if 5 Photos AND 2 Videos limit is reached
    const isFull = photos.length >= 5 && videos.length >= 2;

    const handleViewPhoto = (url) => {
        setActivePhotoUrl(url);
        setShowPhotoModal(true);
    };

    const handlePlayVideo = (url) => {
        setActiveVideoUrl(url);
        setShowVideoModal(true);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const isVideo = file.mimetype ? file.mimetype.startsWith('video/') : file.type.startsWith('video/');
        const isImage = file.mimetype ? file.mimetype.startsWith('image/') : file.type.startsWith('image/');
        const imagesCount = photos.length;
        const videosCount = videos.length;

        // Validations
        if (isImage && imagesCount >= 5) {
            Swal.fire({ title: 'Limit Reached!', text: 'You can upload a maximum of 5 photos.', icon: 'warning', background: '#0E1124', color: '#fff', confirmButtonColor: '#DC2626' });
            return;
        }

        if (isVideo) {
            if (videosCount >= 2) {
                Swal.fire({ title: 'Limit Reached!', text: 'You can upload a maximum of 2 videos.', icon: 'warning', background: '#0E1124', color: '#fff', confirmButtonColor: '#DC2626' });
                return;
            }
            if (file.size > 30 * 1024 * 1024) {
                Swal.fire({ title: 'File Too Large!', text: 'Video file size must not exceed 30MB.', icon: 'warning', background: '#0E1124', color: '#fff', confirmButtonColor: '#DC2626' });
                return;
            }
        }

        const formData = new FormData();
        formData.append('mediaFile', file);

        setUploading(true);

        // 🟢 Show SweetAlert Loading popup during upload
        Swal.fire({
            title: 'Uploading Portfolio...',
            text: 'Please wait while your media file is uploading.',
            background: '#0E1124',
            color: '#fff',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const res = await api.post('/talent-dashboard/media-gallery/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.success) {
                Swal.fire({ title: 'Success!', text: 'File uploaded successfully!', icon: 'success', background: '#0E1124', color: '#fff', confirmButtonColor: '#10B981' });
                fetchMedia();
            }
        } catch (err) {
            Swal.fire({ title: 'Failed!', text: err.response?.data?.message || 'Upload failed.', icon: 'error', background: '#0E1124', color: '#fff', confirmButtonColor: '#DC2626' });
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            background: '#0E1124',
            color: '#fff',
            confirmButtonColor: '#DC2626',
            cancelButtonColor: '#1E2342',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const res = await api.delete(`/talent-dashboard/media-gallery/${id}`);
                if (res.data.success) {
                    Swal.fire({ title: 'Deleted!', text: 'Your file has been deleted.', icon: 'success', background: '#0E1124', color: '#fff', confirmButtonColor: '#10B981' });
                    fetchMedia();
                }
            } catch (err) {
                Swal.fire({ title: 'Failed!', text: 'Failed to delete file.', icon: 'error', background: '#0E1124', color: '#fff', confirmButtonColor: '#DC2626' });
            }
        }
    };

    return (
        <div className="container-fluid py-4 media-gallery-container text-start position-relative">
            
            {/* 🟢 Fullscreen Blur Loading Overlay during file upload */}
            {uploading && (
                <div className="upload-loading-overlay">
                    <div className="text-center p-4 rounded-4 border border-secondary shadow-lg bg-dark bg-opacity-95" style={{ minWidth: '280px' }}>
                        <Spinner animation="border" variant="danger" style={{ width: '3.5rem', height: '3.5rem', borderWidth: '0.25em' }} />
                        <h5 className="mt-3 text-white fw-bold mb-1">Uploading Portfolio...</h5>
                        <p className="text-secondary small mb-0">Please wait while your file is processing</p>
                    </div>
                </div>
            )}

            <div className="mb-4 d-flex justify-content-between align-items-center">
                <div>
                    <h2 className="media-gallery-title">Media Gallery</h2>
                    <p className="media-gallery-subtitle">Upload and showcase your portfolio pictures and audition clips</p>
                </div>
                <div>
                    {!isFull && (
                        <>
                            <Button 
                                variant="danger" 
                                onClick={() => fileInputRef.current.click()} 
                                disabled={uploading} 
                                style={{ backgroundColor: '#DC2626', border: 'none' }}
                            >
                                {uploading ? <Spinner size="sm" className="me-2" /> : <Camera size={18} className="me-2" />}
                                {uploading ? 'Uploading...' : 'Upload Portfolio'}
                            </Button>
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                onChange={handleFileChange} 
                                style={{ display: 'none' }} 
                                accept="image/*,video/*" 
                                disabled={uploading}
                            />
                        </>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>
            ) : (
                <Row className="g-4">
                    {/* Photos Column */}
                    <Col lg={6}>
                        <Card className="border-0 p-4 media-gallery-card text-white h-100">
                            <h5 className="fw-bold border-bottom border-secondary pb-3 mb-4 d-flex align-items-center gap-2">
                                <Image size={20} className="text-danger" /> Photos ({photos.length} / 5)
                            </h5>
                            {photos.length > 0 ? (
                                <div className="media-gallery-grid">
                                    {photos.map(item => (
                                        <div key={item.id} className="media-item-box" onClick={() => handleViewPhoto(item.media_url)}>
                                            <img src={item.media_url} alt={item.file_name} className="media-item-preview" />
                                            <div className="media-delete-overlay d-flex align-items-center justify-content-center gap-3">
                                                <span className="p-2 rounded-circle bg-dark bg-opacity-75 text-white" title="Preview Photo">
                                                    <Eye size={22} />
                                                </span>
                                                <span className="p-2 rounded-circle bg-dark bg-opacity-75 text-danger" title="Delete Photo" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}>
                                                    <Trash2 size={22} />
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center p-5 border border-dashed rounded-3 text-secondary">
                                    No photos uploaded yet. (Max 5 allowed)
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Videos Column */}
                    <Col lg={6}>
                        <Card className="border-0 p-4 media-gallery-card text-white h-100">
                            <h5 className="fw-bold border-bottom border-secondary pb-3 mb-4 d-flex align-items-center gap-2">
                                <Film size={20} className="text-danger" /> Videos ({videos.length} / 2)
                            </h5>
                            {videos.length > 0 ? (
                                <div className="media-gallery-grid">
                                    {videos.map(item => (
                                        <div key={item.id} className="media-item-box" onClick={() => handlePlayVideo(item.media_url)}>
                                            <video src={item.media_url} className="media-item-preview" muted />
                                            <div className="media-delete-overlay d-flex align-items-center justify-content-center gap-3">
                                                <span className="p-2 rounded-circle bg-danger text-white" title="Play Video">
                                                    <Play size={22} fill="currentColor" />
                                                </span>
                                                <span className="p-2 rounded-circle bg-dark bg-opacity-75 text-danger" title="Delete Video" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}>
                                                    <Trash2 size={22} />
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center p-5 border border-dashed rounded-3 text-secondary">
                                    No audition clips uploaded yet. (Max 2 allowed, max 30MB each)
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            )}

            {/* 🟢 Photo Preview Lightbox Modal */}
            <Modal 
                show={showPhotoModal} 
                onHide={() => setShowPhotoModal(false)} 
                centered 
                contentClassName="bg-dark border-0 rounded-4 overflow-hidden shadow-lg"
            >
                <Modal.Header className="border-secondary bg-black p-3 d-flex align-items-center justify-content-between">
                    <Modal.Title className="text-white fs-6 d-flex align-items-center gap-2">
                        <Image size={18} className="text-danger" /> Photo Preview
                    </Modal.Title>
                    <Button variant="dark" className="p-1 rounded-circle border-secondary bg-transparent" onClick={() => setShowPhotoModal(false)}>
                        <X size={18} className="text-white" />
                    </Button>
                </Modal.Header>
                <Modal.Body className="p-2 bg-black text-center d-flex align-items-center justify-content-center" style={{ minHeight: '350px', maxHeight: '80vh' }}>
                    {activePhotoUrl && (
                        <img src={activePhotoUrl} className="mw-100 mh-100 object-fit-contain rounded" style={{ maxHeight: '70vh' }} alt="Preview" />
                    )}
                </Modal.Body>
            </Modal>

            {/* 🟢 Video Playback Lightbox Modal */}
            <Modal 
                show={showVideoModal} 
                onHide={() => setShowVideoModal(false)} 
                centered 
                contentClassName="bg-dark border-0 rounded-4 overflow-hidden shadow-lg"
            >
                <Modal.Header className="border-secondary bg-black p-3 d-flex align-items-center justify-content-between">
                    <Modal.Title className="text-white fs-6 d-flex align-items-center gap-2">
                        <Film size={18} className="text-danger" /> Audition Video Playback
                    </Modal.Title>
                    <Button variant="dark" className="p-1 rounded-circle border-secondary bg-transparent" onClick={() => setShowVideoModal(false)}>
                        <X size={18} className="text-white" />
                    </Button>
                </Modal.Header>
                <Modal.Body className="p-0 bg-black text-center" style={{ height: '420px' }}>
                    {activeVideoUrl && (
                        <video src={activeVideoUrl} className="w-100 h-100 object-fit-contain" controls autoPlay />
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default MediaGallery;